import React from 'react';
import { Users } from 'lucide-react';
import type { MetricasFaturamento, MetricasUsuario } from '../../../../src/lib/financeiro/metricasFaturamento';
import { fmtDias, fmtPct } from './KpiCardsFaturamento';

interface Props {
  metricas: MetricasFaturamento;
}

const corDaTaxa = (taxa: number | null) => {
  if (taxa === null) return 'text-gray-400 dark:text-gray-500';
  if (taxa > 0.2) return 'text-red-600 dark:text-red-400';
  if (taxa >= 0.1) return 'text-amber-600 dark:text-amber-400';
  return 'text-emerald-600 dark:text-emerald-400';
};

const Linha: React.FC<{ m: MetricasUsuario; destaque?: boolean }> = ({ m, destaque }) => (
  <tr className={destaque
    ? 'bg-gray-50 dark:bg-gray-900/40 font-semibold'
    : 'border-t border-gray-100 dark:border-gray-700'}>
    <td className="py-2.5 px-3 text-sm text-gray-900 dark:text-white truncate max-w-[220px]" title={m.usuario}>
      {m.usuario}
    </td>
    <td className="py-2.5 px-3 text-sm text-right text-gray-700 dark:text-gray-300 tabular-nums">
      {m.totalEmitidos.toLocaleString('pt-BR')}
    </td>
    <td className="py-2.5 px-3 text-sm text-right text-gray-700 dark:text-gray-300 tabular-nums">
      {/* Absoluto e taxa juntos: sem o denominador, quem fatura mais parece pior. */}
      {m.foraDoPrazo.toLocaleString('pt-BR')}
    </td>
    <td className={`py-2.5 px-3 text-sm text-right tabular-nums font-semibold ${corDaTaxa(m.taxaForaDoPrazo)}`}>
      {fmtPct(m.taxaForaDoPrazo)}
    </td>
    <td className="py-2.5 px-3 text-sm text-right text-gray-700 dark:text-gray-300 tabular-nums">
      {fmtDias(m.tempoMedioDias)}
      {m.comTempoMedido !== m.totalEmitidos && (
        <span className="block text-[10px] font-normal text-gray-400 dark:text-gray-500">
          base: {m.comTempoMedido}
        </span>
      )}
    </td>
  </tr>
);

const TabelaPorUsuario: React.FC<Props> = ({ metricas }) => (
  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
    <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Por responsável</h3>

    {metricas.porUsuario.length === 0 ? (
      <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
        <Users size={22} className="text-gray-300 dark:text-gray-600" />
        <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">Nenhuma emissão no período</p>
        <p className="text-xs text-gray-400 dark:text-gray-500 max-w-xs">
          Amplie o período ou verifique se o time de Faturamento está com o
          departamento preenchido no perfil.
        </p>
      </div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[520px]">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <th className="py-2 px-3 text-left font-semibold">Responsável</th>
              <th className="py-2 px-3 text-right font-semibold">Faturados</th>
              <th className="py-2 px-3 text-right font-semibold">Fora do prazo</th>
              <th className="py-2 px-3 text-right font-semibold">Taxa</th>
              <th className="py-2 px-3 text-right font-semibold">Tempo médio</th>
            </tr>
          </thead>
          <tbody>
            {metricas.porUsuario.map((m) => <Linha key={m.usuario} m={m} />)}
            <Linha m={metricas.departamento} destaque />
          </tbody>
        </table>
      </div>
    )}
  </div>
);

export default TabelaPorUsuario;
